"use client";

import { useState, useEffect, useContext } from "react";
// eslint-disable-next-line import/named
import { useForm } from "react-hook-form";
import { trans } from "@/service/i18n/i18n";
import { useImmer } from "use-immer";
import { handleUserError } from "@/service/client-api/error-handler";
import { preSubmit } from "@/forms/validation";
import { createFormFields } from "./model";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { clientGet, clientPatch } from "@/service/client-api/client-api";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import {
  canHaveSpecialization,
  isInternOrWithoutSpecialization,
} from "@/service/user-groups";
import { CheckboxField } from "@/forms/fields/checkbox-field/checkbox-field";
import { Form } from "@/components/ui/form";
import { LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { cn } from "@/lib/utils";

export const AsDoctor = ({
  isNewsletterRequired,
  access,
  setAccess,
  redirectTo,
}: {
  isNewsletterRequired: boolean;
  access: any;
  setAccess: any;
  redirectTo: string;
}) => {
  const user = useContext(UserContext);

  const [fields, setFields] = useImmer(
    createFormFields(user.newsletter, isNewsletterRequired),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openedSpecialization, setOpenedSpecialization] = useState(null);
  const [status, setStatus] = useState("");
  const [statusTranslation, setStatusTranslation] = useState("");

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { setValue, handleSubmit } = form;

  const onSubmit = async (values: Record<keyof typeof fields, any>) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    const payload = {} as Record<keyof typeof fields, any>;

    if (values) {
      payload.newsletter = values.newsletter;
    }

    try {
      await clientPatch("profile.update", {}, payload);

      if (redirectTo) {
        window.location = redirectTo;
      } else {
        window.location.reload();
      }
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields);
    }
  };

  const actualizeState = () => {
    setAccess((draft) => {
      draft.previousName = "review_status";

      if (!status || (status && isInternOrWithoutSpecialization(status))) {
        draft.name = "if_doctor_complete_doctor_status";
        return;
      }
      draft.name = "if_specialization";
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      const { dataForType, type }: UserVM = await clientGet("profile.info");
      const { doctor, dentist } = await clientGet("profile.data_info");
      setStatus(dataForType["status"]);
      setStatusTranslation(
        Object.keys(doctor.status).find(
          (key) => doctor.status[key] === dataForType["status"],
        ),
      );

      if (type === "doctor" && dataForType) {
        const openedSpec = Object.keys(doctor.specialization).find(
          (key) =>
            doctor.specialization[key] === dataForType["specializationsByUser"],
        );
        setOpenedSpecialization(openedSpec);
      }
      if (type === "dentist" && dataForType.specializationsByUser) {
        const openedSpec = Object.keys(dentist.specialization).find(
          (key) =>
            dentist.specialization[key] ===
            dataForType["specializationsByUser"],
        );
        setOpenedSpecialization(openedSpec);
      }

      setValue("newsletter", user["newsletter"]);

      setAccess((draft) => {
        draft.studentUpdate = true;
      });
    };

    fetchData();
  }, []);

  if (Object.keys(fields).length === 0) {
    return null;
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2
          className={`text-card-foreground mb-6 text-center text-2xl font-semibold`}
        >
          {trans("hello")} {user.shortName},
        </h2>

        <p className={`text-foreground mb-6 text-center text-base font-normal`}>
          {access.alreadyHasAccess
            ? trans("permission.access_as.title")
            : trans("permission.access_as.check_status")}
        </p>

        <div className="mb-6 rounded-md border p-4">
          <div className="mb-4 text-center text-sm">
            {status
              ? statusTranslation
              : trans("permission.access_as.no_doctor_status")}
          </div>
          {status && canHaveSpecialization(status) && (
            <>
              {!openedSpecialization && (
                <div className="mb-4 text-zinc-600">
                  {trans("permission.access_as.no_specialization")}
                </div>
              )}
              {openedSpecialization && (
                <div className="text-remedium-blue mb-4">
                  {trans("permission.access_as.opened_specialization")} :{" "}
                  {openedSpecialization}
                </div>
              )}
            </>
          )}

          <div
            className={cn(
              "group text-remedium-blue mt-4 flex cursor-pointer items-center justify-center font-bold hover:underline",
              "[&_svg]:ml-4 [&_svg]:opacity-0 [&_svg]:transition-opacity [&_svg]:duration-250 [&_svg]:group-hover:opacity-100",
            )}
            onClick={actualizeState}
          >
            {!status
              ? trans("permission.access_as.set_status")
              : isInternOrWithoutSpecialization(status)
              ? trans("permission.access_as.update_status")
              : openedSpecialization
              ? trans("permission.access_as.edit_specialization")
              : trans("permission.access_as.i_have_specialization")}
          </div>
        </div>

        {!user.defaultNewsletter.isSignedUp && fields.newsletter && (
          <CheckboxField field={fields.newsletter} />
        )}

        <Button className={"mb-4 w-full"} type="submit">
          {loading && <LoaderIcon className={"animate-spin"} size={16} />}
          {trans("next")}
        </Button>
        <FormError error={error} />
      </form>
    </Form>
  );
};
